import { db, storage } from './config';
import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc, 
  writeBatch 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  getBlob
} from 'firebase/storage';
import ImageCompressionUtils from './ImageCompressionUtils';

/**
 * Servicio para migrar y comprimir todas las imágenes existentes en Firebase
 * VERSIÓN CORREGIDA - Sin problemas de CORS
 */
class ImageMigrationService {
  
  /**
   * Migrar todas las imágenes de productos y categorías
   */
  static async migrateAllImages(options = {}) {
    const config = {
      batchSize: 3, // Procesar de 3 en 3 para no sobrecargar
      deleteOriginals: false, // Por seguridad, por defecto no eliminar originales
      createThumbnails: true,
      dryRun: false, // Modo prueba sin hacer cambios reales
      ...options
    };

    console.log('🚀 Iniciando migración de imágenes...', config);
    
    const results = {
      products: { processed: 0, success: 0, errors: [] },
      categories: { processed: 0, success: 0, errors: [] },
      totalSavings: 0
    };

    try {
      // Migrar imágenes de productos
      console.log('📦 Migrando imágenes de productos...');
      const productsResult = await ImageMigrationService.migrateProductImages(config);
      results.products = productsResult;

      // Migrar imágenes de categorías
      console.log('📂 Migrando imágenes de categorías...');
      const categoriesResult = await ImageMigrationService.migrateCategoryImages(config);
      results.categories = categoriesResult;

      // Calcular ahorros totales
      results.totalSavings = productsResult.totalSavings + categoriesResult.totalSavings;

      console.log('✅ Migración completada:', results);
      return results;

    } catch (error) {
      console.error('❌ Error durante la migración:', error);
      throw error;
    }
  }

  /**
   * Migrar imágenes de productos
   */
  static async migrateProductImages(config) {
    const results = { processed: 0, success: 0, errors: [], totalSavings: 0 };
    
    try {
      const productsRef = collection(db, 'products');
      const querySnapshot = await getDocs(productsRef);
      
      const productsWithImages = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(product => product.image && !product.image.includes('_opt.webp') && !product.imageOptimized);

      console.log(`🔍 Encontrados ${productsWithImages.length} productos con imágenes para migrar`);

      // Procesar en lotes
      for (let i = 0; i < productsWithImages.length; i += config.batchSize) {
        const batch = productsWithImages.slice(i, i + config.batchSize);
        
        await Promise.all(
          batch.map(async (product) => {
            try {
              results.processed++;
              console.log(`📸 Procesando producto: ${product.name} (${results.processed}/${productsWithImages.length})`);
              
              const migrationResult = await ImageMigrationService.migrateProductImage(product, config);
              
              if (migrationResult.success) {
                results.success++;
                results.totalSavings += migrationResult.savings || 0;
                console.log(`✅ Producto migrado: ${product.name} - Ahorro: ${migrationResult.savings}KB`);
              } else {
                results.errors.push(`Error en ${product.name}: ${migrationResult.error}`);
              }
              
            } catch (error) {
              results.errors.push(`Error procesando ${product.name}: ${error.message}`);
              console.error(`❌ Error procesando ${product.name}:`, error);
            }
          })
        );
        
        // Pausa entre lotes para no sobrecargar
        if (i + config.batchSize < productsWithImages.length) {
          console.log('⏸️ Pausa entre lotes...');
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
    } catch (error) {
      console.error('❌ Error migrando productos:', error);
      throw error;
    }
    
    return results;
  }

  /**
   * Migrar imágenes de categorías
   */
  static async migrateCategoryImages(config) {
    const results = { processed: 0, success: 0, errors: [], totalSavings: 0 };
    
    try {
      const categoriesRef = collection(db, 'categories');
      const querySnapshot = await getDocs(categoriesRef);
      
      const categoriesWithImages = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(category => category.image && !category.image.includes('_opt.webp') && !category.imageOptimized);

      console.log(`🔍 Encontradas ${categoriesWithImages.length} categorías con imágenes para migrar`);

      for (let i = 0; i < categoriesWithImages.length; i += config.batchSize) {
        const batch = categoriesWithImages.slice(i, i + config.batchSize);
        
        await Promise.all(
          batch.map(async (category) => {
            try {
              results.processed++;
              console.log(`📸 Procesando categoría: ${category.name} (${results.processed}/${categoriesWithImages.length})`);
              
              const migrationResult = await ImageMigrationService.migrateCategoryImage(category, config);
              
              if (migrationResult.success) {
                results.success++;
                results.totalSavings += migrationResult.savings || 0;
                console.log(`✅ Categoría migrada: ${category.name} - Ahorro: ${migrationResult.savings}KB`);
              } else {
                results.errors.push(`Error en ${category.name}: ${migrationResult.error}`);
              }
              
            } catch (error) {
              results.errors.push(`Error procesando ${category.name}: ${error.message}`);
              console.error(`❌ Error procesando ${category.name}:`, error);
            }
          })
        );
        
        if (i + config.batchSize < categoriesWithImages.length) {
          console.log('⏸️ Pausa entre lotes...');
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
    } catch (error) {
      console.error('❌ Error migrando categorías:', error);
      throw error;
    }
    
    return results;
  }

  /**
   * 🆕 FUNCIÓN CORREGIDA: Descargar imagen usando Firebase Storage SDK (sin CORS)
   */
  static async downloadImageFromFirebaseStorage(imageUrl) {
    try {
      console.log(`📥 Descargando imagen desde Firebase Storage...`);
      
      // Extraer la ruta del archivo desde la URL de Firebase Storage
      const urlParts = imageUrl.split('/o/')[1];
      if (!urlParts) {
        throw new Error('URL de Firebase Storage inválida');
      }
      
      const filePath = decodeURIComponent(urlParts.split('?')[0]);
      console.log(`📁 Ruta del archivo: ${filePath}`);
      
      // Crear referencia al archivo en Firebase Storage
      const fileRef = ref(storage, filePath);
      
      // Descargar como blob usando el SDK de Firebase (evita CORS)
      const blob = await getBlob(fileRef);
      console.log(`✅ Descarga exitosa: ${Math.round(blob.size / 1024)}KB`);
      
      return blob;
    } catch (error) {
      console.error('❌ Error descargando desde Firebase Storage:', error);
      
      // Fallback: intentar con fetch si el SDK falla
      try {
        console.log('🔄 Intentando fallback con fetch...');
        const response = await fetch(imageUrl, {
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        console.log(`✅ Fallback exitoso: ${Math.round(blob.size / 1024)}KB`);
        return blob;
      } catch (fetchError) {
        console.error('❌ Fallback también falló:', fetchError);
        throw new Error(`No se pudo descargar la imagen: ${error.message}`);
      }
    }
  }

  /**
   * Migrar imagen individual de producto
   */
  static async migrateProductImage(product, config) {
    try {
      if (config.dryRun) {
        console.log(`🧪 [DRY RUN] Simulando migración de: ${product.name}`);
        return { 
          success: true, 
          message: 'Dry run - no se realizaron cambios',
          savings: Math.round(Math.random() * 1000) // Simulación
        };
      }

      // 🆕 Usar Firebase Storage SDK en lugar de fetch
      const originalBlob = await ImageMigrationService.downloadImageFromFirebaseStorage(product.image);
      
      if (!originalBlob) {
        return { success: false, error: 'No se pudo descargar la imagen original' };
      }

      const originalSize = originalBlob.size;
      console.log(`📊 Tamaño original: ${Math.round(originalSize / 1024)}KB`);

      // Crear archivo temporal para procesamiento
      const tempFile = new File([originalBlob], 'temp.jpg', { type: originalBlob.type });
      
      // Comprimir imagen
      const compressed = await ImageCompressionUtils.compressImageFile(tempFile, {
        MAX_WIDTH: 800,
        MAX_HEIGHT: 600,
        QUALITY: 0.85
      });

      const compressedSize = compressed.size;
      const savings = Math.round((originalSize - compressedSize) / 1024); // KB ahorrados
      console.log(`📊 Tamaño comprimido: ${Math.round(compressedSize / 1024)}KB (${savings}KB ahorrados)`);

      // Generar nombres optimizados
      const compressedFileName = ImageCompressionUtils.generateOptimizedFileName(
        `product_${product.id}`, true, false
      );

      // Subir imagen comprimida
      const compressedRef = ref(storage, `products/${compressedFileName}`);
      await uploadBytes(compressedRef, compressed);
      const newImageUrl = await getDownloadURL(compressedRef);
      console.log(`✅ Imagen comprimida subida: ${newImageUrl}`);

      // Crear thumbnail si está configurado
      let thumbnailUrl = null;
      if (config.createThumbnails) {
        const thumbnail = await ImageCompressionUtils.createThumbnail(tempFile);
        const thumbnailFileName = ImageCompressionUtils.generateOptimizedFileName(
          `product_${product.id}`, true, true
        );
        
        const thumbnailRef = ref(storage, `products/thumbnails/${thumbnailFileName}`);
        await uploadBytes(thumbnailRef, thumbnail);
        thumbnailUrl = await getDownloadURL(thumbnailRef);
        console.log(`✅ Thumbnail creado: ${thumbnailUrl}`);
      }

      // Actualizar documento en Firestore
      const updateData = { 
        image: newImageUrl,
        originalImageBackup: product.image, // Backup de la URL original
        imageOptimized: true,
        optimizedAt: new Date().toISOString(),
        imageOptimization: {
          originalSize,
          compressedSize,
          savings: Math.round(((originalSize - compressedSize) / originalSize) * 100)
        }
      };

      if (thumbnailUrl) {
        updateData.thumbnailImage = thumbnailUrl;
      }

      const productRef = doc(db, 'products', product.id);
      await updateDoc(productRef, updateData);
      console.log(`✅ Documento actualizado en Firestore`);

      // Eliminar imagen original si está configurado
      if (config.deleteOriginals && product.image.includes('firebasestorage')) {
        try {
          const urlParts = product.image.split('/o/')[1];
          const filePath = decodeURIComponent(urlParts.split('?')[0]);
          const oldRef = ref(storage, filePath);
          await deleteObject(oldRef);
          console.log(`🗑️ Imagen original eliminada: ${product.image}`);
        } catch (error) {
          console.warn('⚠️ No se pudo eliminar imagen original:', error.message);
        }
      }

      return { 
        success: true, 
        savings,
        originalSize: Math.round(originalSize / 1024),
        compressedSize: Math.round(compressedSize / 1024),
        newUrl: newImageUrl,
        thumbnailUrl
      };

    } catch (error) {
      console.error(`❌ Error migrando imagen de producto ${product.id}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Migrar imagen individual de categoría
   */
  static async migrateCategoryImage(category, config) {
    try {
      if (config.dryRun) {
        console.log(`🧪 [DRY RUN] Simulando migración de: ${category.name}`);
        return { 
          success: true, 
          message: 'Dry run - no se realizaron cambios',
          savings: Math.round(Math.random() * 500) // Simulación
        };
      }

      // 🆕 Usar Firebase Storage SDK
      const originalBlob = await ImageMigrationService.downloadImageFromFirebaseStorage(category.image);
      
      if (!originalBlob) {
        return { success: false, error: 'No se pudo descargar la imagen original' };
      }

      const originalSize = originalBlob.size;
      const tempFile = new File([originalBlob], 'temp.jpg', { type: originalBlob.type });
      
      const compressed = await ImageCompressionUtils.compressImageFile(tempFile);
      const compressedSize = compressed.size;
      const savings = Math.round((originalSize - compressedSize) / 1024);

      const compressedFileName = ImageCompressionUtils.generateOptimizedFileName(
        `category_${category.id}`, true, false
      );

      const compressedRef = ref(storage, `categories/${compressedFileName}`);
      await uploadBytes(compressedRef, compressed);
      const newImageUrl = await getDownloadURL(compressedRef);

      // Thumbnail para categoría
      let thumbnailUrl = null;
      if (config.createThumbnails) {
        const thumbnail = await ImageCompressionUtils.createThumbnail(tempFile, {
          MAX_WIDTH: 150,
          MAX_HEIGHT: 150
        });
        
        const thumbnailFileName = ImageCompressionUtils.generateOptimizedFileName(
          `category_${category.id}`, true, true
        );
        
        const thumbnailRef = ref(storage, `categories/thumbnails/${thumbnailFileName}`);
        await uploadBytes(thumbnailRef, thumbnail);
        thumbnailUrl = await getDownloadURL(thumbnailRef);
      }

      const updateData = { 
        image: newImageUrl,
        originalImageBackup: category.image,
        imageOptimized: true,
        optimizedAt: new Date().toISOString(),
        imageOptimization: {
          originalSize,
          compressedSize,
          savings: Math.round(((originalSize - compressedSize) / originalSize) * 100)
        }
      };

      if (thumbnailUrl) {
        updateData.thumbnailImage = thumbnailUrl;
      }

      const categoryRef = doc(db, 'categories', category.id);
      await updateDoc(categoryRef, updateData);

      if (config.deleteOriginals && category.image.includes('firebasestorage')) {
        try {
          const urlParts = category.image.split('/o/')[1];
          const filePath = decodeURIComponent(urlParts.split('?')[0]);
          const oldRef = ref(storage, filePath);
          await deleteObject(oldRef);
          console.log(`🗑️ Imagen original eliminada: ${category.image}`);
        } catch (error) {
          console.warn('⚠️ No se pudo eliminar imagen original:', error.message);
        }
      }

      return { 
        success: true, 
        savings,
        originalSize: Math.round(originalSize / 1024),
        compressedSize: Math.round(compressedSize / 1024),
        newUrl: newImageUrl,
        thumbnailUrl
      };

    } catch (error) {
      console.error(`❌ Error migrando imagen de categoría ${category.id}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Obtener estadísticas de imágenes actuales
   */
  static async getImageStats() {
    try {
      const stats = {
        products: { total: 0, optimized: 0, needsMigration: 0, totalSize: 0 },
        categories: { total: 0, optimized: 0, needsMigration: 0, totalSize: 0 }
      };

      // Estadísticas de productos
      const productsRef = collection(db, 'products');
      const productsSnapshot = await getDocs(productsRef);
      
      for (const docSnapshot of productsSnapshot.docs) {
        const product = docSnapshot.data();
        if (product.image) {
          stats.products.total++;
          if (product.imageOptimized) {
            stats.products.optimized++;
          } else {
            stats.products.needsMigration++;
          }
        }
      }

      // Estadísticas de categorías
      const categoriesRef = collection(db, 'categories');
      const categoriesSnapshot = await getDocs(categoriesRef);
      
      for (const docSnapshot of categoriesSnapshot.docs) {
        const category = docSnapshot.data();
        if (category.image) {
          stats.categories.total++;
          if (category.imageOptimized) {
            stats.categories.optimized++;
          } else {
            stats.categories.needsMigration++;
          }
        }
      }

      return stats;
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      throw error;
    }
  }

  /**
   * Limpiar imágenes de respaldo después de confirmar migración
   */
  static async cleanupBackupImages() {
    console.log('🧹 Iniciando limpieza de imágenes de respaldo...');
    
    const results = { cleaned: 0, errors: [] };

    try {
      // Limpiar productos
      const productsRef = collection(db, 'products');
      const productsSnapshot = await getDocs(productsRef);
      
      for (const docSnapshot of productsSnapshot.docs) {
        const product = docSnapshot.data();
        
        if (product.originalImageBackup && product.imageOptimized) {
          try {
            await updateDoc(docSnapshot.ref, {
              originalImageBackup: null
            });
            results.cleaned++;
          } catch (error) {
            results.errors.push(`Error limpiando producto ${docSnapshot.id}: ${error.message}`);
          }
        }
      }

      // Limpiar categorías
      const categoriesRef = collection(db, 'categories');
      const categoriesSnapshot = await getDocs(categoriesRef);
      
      for (const docSnapshot of categoriesSnapshot.docs) {
        const category = docSnapshot.data();
        
        if (category.originalImageBackup && category.imageOptimized) {
          try {
            await updateDoc(docSnapshot.ref, {
              originalImageBackup: null
            });
            results.cleaned++;
          } catch (error) {
            results.errors.push(`Error limpiando categoría ${docSnapshot.id}: ${error.message}`);
          }
        }
      }

      console.log('✅ Limpieza completada:', results);
      return results;

    } catch (error) {
      console.error('❌ Error durante limpieza:', error);
      throw error;
    }
  }
}

export default ImageMigrationService;